import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Ship, Anchor, Database, Thermometer, Percent, Snowflake, Scale, Zap } from "lucide-react";

const UKMRVCalculations = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const coreCalculations = [
    {
      id: "fuel-consumption-aggregation",
      title: "Fuel Consumption Aggregation",
      description: "Sums fuel consumption from all vessel systems across 5+ engine types",
      formula: "Total Fuel = Σ(Main Engine + Aux Engine + Boiler + Incinerator + IGG)",
      subFormulas: [
        "Main_Engine_Cons_MT_${fuelType}",
        "Aux_Engine_Cons_MT_${fuelType}",
        "Boiler_Cons_MT_${fuelType}",
        "Incinerator_Cons_MT_${fuelType}",
        "IGG_Cons_MT_${fuelType}",
        "Supports HFO, LFO, MDO, MGO, LNG fuels",
        "Dynamic field lookup based on vessel fuel types"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "emission-calculation-with-cslip",
      title: "Emission Calculation (with CSlip)",
      description: "Calculates CO₂, CH₄, N₂O using database factors with methane slip",
      formula: "CO₂ = Fuel × CF_CO₂, CH₄ = Fuel × CF_CH₄ + (Fuel × CSlip%), N₂O = Fuel × CF_N₂O",
      subFormulas: [
        "CF_CO₂, CF_CH₄, CF_N₂O from FuelTable database",
        "CSlip applied only to CH₄ emissions",
        "Emergency fallback to default factors if database missing",
        "CSlip values: HFO/LFO/MDO/MGO=0%, LNG=0.2%"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "ghg-calculation-gwp",
      title: "Total GHG Calculation (GWP)",
      description: "Calculates total greenhouse gas emissions using Global Warming Potentials",
      formula: "GHG (t CO₂e) = CO₂ + (CH₄ × 28) + (N₂O × 265)",
      subFormulas: [
        "GWP_CO₂ = 1",
        "GWP_CH₄ = 28 (28× CO₂ equivalent)",
        "GWP_N₂O = 265 (265× CO₂ equivalent)",
        "Separate totals: total emissions vs ETS-covered emissions",
        "Units: Metric Tons CO₂ equivalent"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "uk-mrv-coverage-percentage",
      title: "UK MRV Coverage Percentage",
      description: "Determines percentage of emissions covered by UK ETS based on voyage type",
      formula: "MRV Fraction = getUKMRVFraction(segmentType)",
      subFormulas: [
        "100% coverage: at_uk_port, between_uk_ports",
        "50% coverage: from_uk_port, to_uk_port",
        "0% coverage: outside_uk_scope",
        "Applied to ETS emission calculations only",
        "Separate from total emissions calculation"
      ],
      icon: <Percent className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    }
  ];

  const segmentClassification = [
    {
      id: "port-classification-uk",
      title: "UK Port Classification",
      description: "Identifies UK ports using PortCompliance collection with caching",
      formula: "isUKPort = PortCompliance.uk === true",
      subFormulas: [
        "Database lookup: portName or portCode match",
        "Case-insensitive regex matching",
        "Port normalization for fuzzy matching",
        "Cache built at startup for performance",
        "Supports port name variations"
      ],
      icon: <Anchor className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "segment-determination-uk",
      title: "UK Segment Determination",
      description: "Classifies voyages into 5 distinct UK MRV segments",
      formula: "segmentType = determineUKSegmentTypeFromRaw(record, ukPortsCache)",
      subFormulas: [
        "at_uk_port: Current port is UK, no voyage",
        "between_uk_ports: Both ports are UK",
        "from_uk_port: UK → Non-UK",
        "to_uk_port: Non-UK → UK",
        "outside_uk_scope: No UK ports involved",
        "Vessel status considered for 'at port' determination"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
  ];

  const complianceCalculations = [
    {
      id: "uk-reduction-factor",
      title: "UK Reduction Factor",
      description: "Annual phase-in factor for UK ETS compliance",
      formula: "Reduction Factor = f(benchmarkYear)",
      subFormulas: [
        "2024: 0.4 (40% of emissions covered)",
        "2025: 0.7 (70% of emissions covered)",
        "Other years: 1.0 (100% coverage)",
        "Applied to ETS-covered emissions only",
        "Based on UK ETS phase-in schedule"
      ],
      icon: <Percent className="w-5 h-5" />,
      borderColor: "border-rose-200",
      bgColor: "bg-rose-50",
      textColor: "text-rose-700",
    },
    {
      id: "uk-ice-class-discount",
      title: "UK Ice Class Discount",
      description: "5% discount for qualifying ice-class vessels until 2030",
      formula: "Ice Discount = qualifiesForIceDiscount ? 0.95 : 1.0",
      subFormulas: [
        "Requirements: iceClassShip = 'YES' AND iceClassType includes 'IA'",
        "Valid until: benchmarkYear ≤ 2030",
        "Discount: 5% (0.95 multiplier)",
        "Applied to final UKAS calculation",
        "IA Super also qualifies"
      ],
      icon: <Snowflake className="w-5 h-5" />,
      borderColor: "border-cyan-200",
      bgColor: "bg-cyan-50",
      textColor: "text-cyan-700",
    },
    {
      id: "ukas-calculation",
      title: "UK Allowances (UKAS) Calculation",
      description: "Final UK Allowances required for compliance",
      formula: "UKAS = Reduction Factor × GHG_MRV × Ice Discount",
      subFormulas: [
        "GHG_MRV = Total ETS-covered emissions",
        "Reduction Factor based on year",
        "Ice Discount if applicable",
        "Units: Metric Tons CO₂ equivalent",
        "Separate from EU ETS calculations"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-gray-200",
      bgColor: "bg-gray-50",
      textColor: "text-gray-700",
    },
    {
      id: "performance-metrics-uk",
      title: "Performance Metrics",
      description: "Calculates operational performance indicators",
      formula: "Transport Work = Cargo Mass (MT) × Distance (NM)",
      subFormulas: [
        "Distance: From Distance Sailed/Travelled fields",
        "Cargo Mass: From Cargo Quantity (MT)",
        "Sea Days: Based on vessel status 'sailing'",
        "Fuel Efficiency: Fuel ÷ Distance",
        "Carbon Intensity: CO₂ ÷ Transport Work"
      ],
      icon: <Scale className="w-5 h-5" />,
      borderColor: "border-yellow-200",
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-700",
    }
  ];

  const dataSources = [
    {
      collection: "VesselRawData",
      fields: [
        "Report Date Local Time", "createdAt",
        "From_Port", "To_Port", "Current_Port_Name", "Status",
        "Main_Engine_Cons_MT_*", "Aux_Engine_Cons_MT_*",
        "Boiler_Cons_MT_*", "Incinerator_Cons_MT_*", "IGG_Cons_MT_*",
        "Distance Sailed (NM)", "Distance Travelled (NM)",
        "Observed_GPS_Distance_nm (Propulsion + Drifting)",
        "Cargo Quantity (MT)", "Zone"
      ],
      purpose: "Raw voyage and consumption data"
    },
    {
      collection: "Port",
      fields: ["portName", "portCode", "uk (boolean)"],
      purpose: "UK port identification (uk = true)",
    },
    {
      collection: "FuelTable",
      fields: ["Fuel_Type", "CF_C02", "CF_CH4", "CF_N20", "CSlip"],
      purpose: "Emission factors with fallback defaults"
    },
    {
      collection: "Vessel",
      fields: ["imoNumber", "vesselType", "fuelTypes", "iceClassShip", "iceClassType"],
      purpose: "Vessel configuration and characteristics"
    }
  ];

  const segmentTypes = [
    { type: "at_uk_port", coverage: "100%", description: "At UK Port (no voyage, status considered)" },
    { type: "between_uk_ports", coverage: "100%", description: "Between UK Ports" },
    { type: "from_uk_port", coverage: "50%", description: "UK → Non-UK" },
    { type: "to_uk_port", coverage: "50%", description: "Non-UK → UK" },
    { type: "outside_uk_scope", coverage: "0%", description: "Outside UK Scope" }
  ];

  const defaultEmissionFactors = {
    HFO: { cf_co2: 3.114, cf_ch4: 0.00005, cf_n2o: 0.00018, cSlip: 0 },
    LFO: { cf_co2: 3.151, cf_ch4: 0.00005, cf_n2o: 0.00018, cSlip: 0 },
    MDO: { cf_co2: 3.206, cf_ch4: 0.00005, cf_n2o: 0.00018, cSlip: 0 },
    MGO: { cf_co2: 3.206, cf_ch4: 0.00005, cf_n2o: 0.00018, cSlip: 0 },
    LNG: { cf_co2: 2.750, cf_ch4: 0.0005, cf_n2o: 0.00018, cSlip: 0.2 }
  };

  const reductionFactors = {
    2024: { factor: 0.4, description: "40% coverage (phase-in year 1)" },
    2025: { factor: 0.7, description: "70% coverage (phase-in year 2)" },
    default: { factor: 1.0, description: "100% coverage (full phase-in)" }
  };

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#b6faed] rounded-lg">
              <Calculator className="w-6 h-6 text-[#117864]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                UK MRV Emissions Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Comprehensive UK Monitoring, Reporting and Verification methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Data Sources</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">{source.collection}</h4>
                    <div className="text-sm text-gray-600 mb-2">{source.purpose}</div>
                    <div className="text-xs text-gray-500 space-y-1">
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">{field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Segment Types & Coverage</h3>
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {segmentTypes.map((segment, index) => (
                    <div key={index} className="bg-white p-3 rounded border">
                      <div className="font-semibold text-gray-800 text-sm">{segment.type}</div>
                      <div className={`text-sm font-medium ${segment.coverage === '100%' ? 'text-green-600' : segment.coverage === '50%' ? 'text-amber-600' : 'text-red-600'}`}>
                        UK ETS Coverage: {segment.coverage}
                      </div>
                      <div className="text-xs text-gray-500">{segment.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">UK ETS Reduction Factors</h3>
              <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(reductionFactors).map(([year, data]) => (
                    <div key={year} className="bg-white p-3 rounded border">
                      <div className="font-semibold text-gray-800">{year === 'default' ? 'Other Years' : `Year ${year}`}</div>
                      <div className="text-2xl font-bold text-rose-600">{data.factor}</div>
                      <div className="text-sm text-gray-600">{data.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Segment Classification</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {segmentClassification.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Logic:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Compliance Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {complianceCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 p-4 bg-teal-50 border border-teal-200 rounded-lg">
              <h4 className="font-semibold text-teal-800 mb-2">Complete UKAS Calculation</h4>
              <div className="text-sm text-teal-700 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 1: GHG Emissions</div>
                    <div className="text-xs font-mono mt-1">GHG = CO₂ + (CH₄×28) + (N₂O×265)</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 2: UK ETS Coverage</div>
                    <div className="text-xs font-mono mt-1">GHG_MRV = GHG × MRV Fraction</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 3: Reduction Factor</div>
                    <div className="text-xs font-mono mt-1">Base UKAS = GHG_MRV × Reduction</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 4: Ice Discount</div>
                    <div className="text-xs font-mono mt-1">Final UKAS = Base UKAS × 0.95</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UKMRVCalculations;